import { IconContext } from '@phosphor-icons/react'
import classNames from 'classnames'
import { useCallback } from 'react'
import z from 'zod'
import { BUTTON_ICON_CONTEXT } from './button-icon-context.ts'
import * as styles from './ghost-button.css.ts'

export const GhostButtonTypeSchema = z.enum(['default', 'secondary', 'destructive'])
export type GhostButtonType = z.infer<typeof GhostButtonTypeSchema>
export const GhostButtonStateSchema = z.enum(['default', 'disabled'])
export type GhostButtonState = z.infer<typeof GhostButtonStateSchema>
export const GhostButtonSizeSchema = z.enum(['default', 'small', 'extra-small'])
export type GhostButtonSize = z.infer<typeof GhostButtonSizeSchema>

type GhostButtonProps = React.PropsWithChildren<{
  type?: GhostButtonType
  state?: GhostButtonState
  size?: GhostButtonSize
  className?: string
  /**
   * Renders as disabled without leaving the tab order: styled inert and ignoring clicks, but still
   * focusable and hoverable. For a control whose refusal comes with an explanation the user has to
   * be able to reach — a tooltip, which a natively `disabled` button can never show a keyboard user.
   * Prefer `state="disabled"` when there is nothing to explain.
   */
  ariaDisabled?: boolean
  ariaLabel?: string
  icon?: React.ReactNode
  'data-testid'?: string
  ref?: React.Ref<HTMLButtonElement>
}> &
  Omit<React.HTMLAttributes<HTMLButtonElement>, 'className'>

export const GhostButton: React.FC<GhostButtonProps> = ({
  children,
  type = 'default',
  state = 'default',
  size = 'default',
  className,
  ariaDisabled,
  ariaLabel,
  icon,
  'data-testid': testId,
  onClick,
  onKeyDown,
  ref,
  ...rest
}) => {
  // Both forms look the same; only one of them is reachable by keyboard.
  const isInert = state === 'disabled' || ariaDisabled === true
  const mergedClassName = classNames(styles.button({ type, state: isInert ? 'disabled' : state, size }), className)

  const handleButtonClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      if (isInert) {
        e.preventDefault()
        return
      }
      onClick?.(e)
    },
    [isInert, onClick]
  )

  const handleMouseDown = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
  }, [])

  // `type="button"`: without it the native default is `type="submit"`, which password managers treat
  // as a login-form submit — e.g. clicking Cancel next to credential fields prompts 1Password to
  // save. Ghost-button actions fire via onClick, never a form submit. It sits after the spread for
  // consistency, not to resolve a conflict: `rest` is `HTMLAttributes`, which declares no `type`.
  return (
    <IconContext.Provider value={BUTTON_ICON_CONTEXT}>
      <button
        ref={ref}
        className={mergedClassName}
        disabled={state === 'disabled'}
        aria-disabled={ariaDisabled ? true : undefined}
        onMouseDown={handleMouseDown}
        onClick={handleButtonClick}
        onKeyDown={onKeyDown}
        aria-label={ariaLabel}
        data-testid={testId}
        {...rest}
        type="button"
      >
        {icon}
        {children}
      </button>
    </IconContext.Provider>
  )
}
